import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, Loader, Monitor, ShieldCheck } from 'lucide-react'
import { Message } from '@/components/sharedDevice/Message'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import { describeUserAgent, normalizePairingCode } from '@/utils/sharedDevice'

interface Preview {
  code: string
  device_name: string
  device_type: string
  user_agent: string | null
  requested_seconds_ago: number
  proposed_scope: 'family' | 'display'
}

type Trust = '30' | '90' | 'none'

const TRUST_OPTIONS: { value: Trust; label: string; hint: string }[] = [
  { value: '30', label: '30 days', hint: 'Pair again after a month' },
  { value: '90', label: '90 days', hint: 'Recommended' },
  { value: 'none', label: 'Until I remove it', hint: 'Remove it any time in Settings > Devices' },
]

function ago(seconds: number): string {
  if (seconds < 60) return 'less than a minute ago'
  const m = Math.round(seconds / 60)
  return m === 1 ? '1 minute ago' : `${m} minutes ago`
}

function errorInfo(err: any): { status: number; message: string; reason: string } {
  return {
    status: err?.response?.status || 0,
    message: err?.response?.data?.message || err?.response?.data?.error || '',
    reason: err?.response?.data?.reason || '',
  }
}

// Preview reasons from HP Phoenix 6.0.166: 404 not_found, 410 expired,
// 409 already_used or ambiguous. Older servers answered 404 for all three.
function previewError(status: number, reason: string): string {
  if (status === 410 || reason === 'expired') {
    return 'This code has expired. Codes last 15 minutes, and the device shows a new one when this one runs out. Scan or enter the new code.'
  }
  if (reason === 'already_used') {
    return 'This code has already been used to pair a device in your family. To manage or remove it, go to Settings > Devices. If the device still shows a code, scan the new one.'
  }
  if (status === 409) {
    return 'More than one device has this code. Enter the full code shown on the device, including the letters before the dash.'
  }
  if (status === 404) {
    return 'No device is showing this code. Check the code on the device screen; it changes every 15 minutes.'
  }
  if (status === 403) return 'Only a parent can approve devices.'
  return 'Could not look up this code. Check your connection and try again.'
}

function approveError(status: number, message: string): string {
  if (status === 400 && /expired/i.test(message)) return 'This code has expired. The device is already showing a new one; scan it again.'
  if (status === 400 && /used/i.test(message)) return 'This code has already been used. If the device still shows a code, scan the new one.'
  if (status === 404) return 'This code was not found. Check the code on the device screen.'
  if (status === 409) return 'More than one device has this code. Enter the full code, including the letters before the dash.'
  if (status === 403) return 'Only a parent can approve devices.'
  return 'The device could not be approved. Try again.'
}

/** /approve?code=W-XXXXXX: a parent approves a device that is showing a code. */
export const ApproveDevicePage: React.FC = () => {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const code = normalizePairingCode(params.get('code') || '')
  const [typed, setTyped] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [trust, setTrust] = useState<Trust>('90')
  const [approving, setApproving] = useState(false)
  const [done, setDone] = useState<{ name: string; trustExpiresAt: string | null; scope: string } | null>(null)

  const isParent = !!user?.is_parent

  useEffect(() => {
    if (!code || !isParent) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setPreview(null)
    apiClient
      .get(`/api/platform/device/preview/${encodeURIComponent(code)}`)
      .then((p: Preview) => {
        if (cancelled) return
        setPreview(p)
        setName(p.device_name || '')
        setTrust(p.proposed_scope === 'family' ? '90' : 'none')
      })
      .catch((err: any) => {
        if (!cancelled) {
          const { status, reason } = errorInfo(err)
          setError(previewError(status, reason))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [code, isParent])

  const approve = async () => {
    if (!preview) return
    setApproving(true)
    setError(null)
    try {
      const r = await apiClient.post('/api/platform/device/approve-code', {
        code: preview.code,
        scope: preview.proposed_scope,
        trust_days: trust === 'none' ? null : Number(trust),
        device_name: name.trim() || preview.device_name,
      })
      setDone({ name: r.device_name || name, trustExpiresAt: r.trust_expires_at || null, scope: r.scope || preview.proposed_scope })
    } catch (err: any) {
      const { status, message } = errorInfo(err)
      setError(approveError(status, message))
    } finally {
      setApproving(false)
    }
  }

  const ua = describeUserAgent(preview?.user_agent)
  const isShared = preview?.proposed_scope === 'family'

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center p-4 py-8">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-3 rounded-xl bg-indigo-100">
            <Monitor className="w-6 h-6 text-indigo-700" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Approve a device</h1>
        </div>

        {!isParent ? (
          <Message tone="warn">
            Only a parent can approve devices. Ask a parent to scan the code on the screen with their phone.
          </Message>
        ) : done ? (
          <div role="status">
            <p className="flex items-start gap-3 text-lg text-gray-900 mb-3">
              <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0" aria-hidden="true" />
              <span>
                <strong>{done.name}</strong> is paired. It will show your family in a few seconds.
              </span>
            </p>
            <p className="text-gray-600 mb-6">
              {done.trustExpiresAt
                ? `It stays paired until ${new Date(done.trustExpiresAt).toLocaleDateString(undefined, { dateStyle: 'long' })}.`
                : 'It stays paired until you remove it.'}{' '}
              You can remove it any time in Settings &gt; Devices.
            </p>
            <Link to="/dashboard" className="inline-flex items-center min-h-[48px] px-5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700">
              Done
            </Link>
          </div>
        ) : !code ? (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const c = normalizePairingCode(typed)
              if (c) setParams({ code: c })
            }}
          >
            <label htmlFor="pair-code" className="block text-gray-700 mb-2">
              Enter the code shown on the TV or tablet
            </label>
            <input
              id="pair-code"
              value={typed}
              onChange={(e) => setTyped(e.target.value.toUpperCase())}
              placeholder="W-ABC234"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              className="w-full px-4 py-3 text-2xl font-mono tracking-widest rounded-xl border-2 border-gray-300 focus:border-indigo-500 outline-none"
            />
            <button
              type="submit"
              disabled={typed.trim().length < 6}
              className="mt-4 w-full min-h-[52px] rounded-xl bg-indigo-600 text-white text-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-indigo-300"
            >
              Continue
            </button>
          </form>
        ) : loading ? (
          <p className="flex items-center gap-3 text-gray-700" role="status">
            <Loader className="w-5 h-5 animate-spin" /> Looking up {code}...
          </p>
        ) : preview ? (
          <>
            <dl className="rounded-xl bg-gray-50 border border-gray-200 divide-y divide-gray-200 mb-5">
              <Row label="Code" value={<span className="font-mono">{preview.code}</span>} />
              <Row label="Browser" value={[ua.browser, ua.os].filter(Boolean).join(' on ') || 'Unknown'} />
              <Row label="Requested" value={ago(preview.requested_seconds_ago)} />
            </dl>

            <label htmlFor="device-name" className="block text-sm font-semibold text-gray-800 mb-1">
              Device name
            </label>
            <input
              id="device-name"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 text-lg rounded-xl border-2 border-gray-300 focus:border-indigo-500 outline-none mb-5"
            />

            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 mb-5 flex gap-3">
              <ShieldCheck className="w-6 h-6 text-indigo-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-indigo-950">
                {isShared ? (
                  <>
                    <p className="font-semibold mb-1">Shared family device</p>
                    <p>
                      Anyone at this screen can tap a child's picture and use HousePoints as that child. Parent
                      actions on it need approval from a parent's phone each time, and rewards redeemed there wait
                      for a parent to approve them.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold mb-1">Display</p>
                    <p>This screen shows your family's dashboard. Nobody can act as a family member on it.</p>
                  </>
                )}
                <p className="mt-2">Only approve a device that is in front of you and showing this code.</p>
              </div>
            </div>

            <fieldset className="mb-6">
              <legend className="text-sm font-semibold text-gray-800 mb-2">Keep it paired for</legend>
              <div className="grid gap-2">
                {TRUST_OPTIONS.map((o) => (
                  <label
                    key={o.value}
                    className={`flex items-center gap-3 min-h-[52px] px-4 rounded-xl border-2 cursor-pointer ${
                      trust === o.value ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="trust"
                      value={o.value}
                      checked={trust === o.value}
                      onChange={() => setTrust(o.value)}
                      className="w-5 h-5"
                    />
                    <span className="font-medium text-gray-900">{o.label}</span>
                    <span className="text-sm text-gray-500 ml-auto text-right">{o.hint}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {error && <Message tone="error">{error}</Message>}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={approve}
                disabled={approving}
                className="flex-1 min-h-[52px] rounded-xl bg-indigo-600 text-white text-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-indigo-300 inline-flex items-center justify-center gap-2"
              >
                {approving && <Loader className="w-5 h-5 animate-spin" />}
                Approve
              </button>
              <Link
                to="/dashboard"
                className="min-h-[52px] px-5 rounded-xl border-2 border-gray-300 text-gray-800 font-semibold inline-flex items-center justify-center hover:bg-gray-50"
              >
                This is not my device
              </Link>
            </div>
          </>
        ) : (
          <>
            {error && <Message tone="error">{error}</Message>}
            <button
              type="button"
              onClick={() => setParams({})}
              className="min-h-[48px] px-5 rounded-xl border-2 border-gray-300 font-semibold hover:bg-gray-50"
            >
              Enter a different code
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 px-4 py-3">
    <dt className="text-gray-500">{label}</dt>
    <dd className="text-gray-900 text-right">{value}</dd>
  </div>
)

export default ApproveDevicePage
